import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, EyeOff, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Stars } from "@/components/Feedback";
import { api, errMsg } from "@/lib/api";

const BADGE = { pending: "bg-amber-500/15 text-amber-300", approved: "bg-emerald-500/15 text-emerald-300", hidden: "bg-slate-800 text-slate-400" };

const Row = ({ t, onSaved }) => {
  const [quote, setQuote] = useState(t.quote);
  const [credit, setCredit] = useState(t.credit);
  const [busy, setBusy] = useState(false);
  const save = async (status) => {
    setBusy(true);
    try { await api.put(`/testimonials/${t.id}`, { status, quote, credit }); onSaved({ ...t, status, quote, credit }); toast.success(status === "approved" ? "Published on the landing page" : status === "hidden" ? "Hidden" : "Moved back to pending"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-6 space-y-4" data-testid={`testimonial-row-${t.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Stars value={5} size="h-4 w-4" />
        <span data-testid={`testimonial-status-${t.id}`} className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${BADGE[t.status]}`}>{t.status}</span>
        <span className="text-sm text-slate-400">{t.client_name} · <Link to={`/studies/${t.study_id}`} className="hover:text-emerald-400">{t.study_name}</Link> · {t.updated_at?.slice(0, 10)}</span>
      </div>
      <p className="text-xs text-slate-500">Original: “{t.comment}”</p>
      <Textarea data-testid={`testimonial-quote-${t.id}`} rows={3} value={quote} onChange={(e) => setQuote(e.target.value)} className="bg-slate-950/60 border-slate-700" />
      <Input data-testid={`testimonial-credit-${t.id}`} value={credit} onChange={(e) => setCredit(e.target.value)} className="bg-slate-950/60 border-slate-700 max-w-sm" />
      <div className="flex flex-wrap gap-2">
        <Button data-testid={`testimonial-approve-${t.id}`} size="sm" disabled={busy} onClick={() => save("approved")} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}{t.status === "approved" ? "Save & keep published" : "Approve & publish"}</Button>
        {t.status !== "hidden" && <Button data-testid={`testimonial-hide-${t.id}`} size="sm" variant="outline" disabled={busy} onClick={() => save("hidden")} className="rounded-full border-slate-700 bg-transparent"><EyeOff className="h-4 w-4 mr-1" />Hide</Button>}
        {t.status !== "pending" && <Button data-testid={`testimonial-pending-${t.id}`} size="sm" variant="ghost" disabled={busy} onClick={() => save("pending")} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Back to pending</Button>}
      </div>
    </div>
  );
};

export default function Testimonials() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("pending");
  useEffect(() => { api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const list = (items || []).filter((t) => t.status === tab);
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 fade-up" data-testid="testimonials-page">
      <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Team</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">Testimonials</h1>
      <p className="text-slate-400 mb-8">5-star ratings with a comment land here. Edit the quote and credit, then approve to show it on the landing page.</p>
      <div className="flex gap-2 mb-6">
        {["pending", "approved", "hidden"].map((s) => (
          <button key={s} data-testid={`testimonials-tab-${s}`} onClick={() => setTab(s)} className={`px-4 py-1.5 rounded-full text-sm border capitalize transition-colors ${tab === s ? "bg-emerald-500 text-emerald-950 border-emerald-500" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
            {s} <span className="num opacity-70">{(items || []).filter((t) => t.status === s).length}</span></button>
        ))}
      </div>
      {items === null ? <Loader2 className="h-6 w-6 animate-spin text-emerald-400" /> : list.length === 0
        ? <p className="rounded-xl border border-dashed border-slate-700 p-12 text-center text-slate-400" data-testid="testimonials-empty">Nothing {tab} right now.</p>
        : <div className="space-y-4">{list.map((t) => <Row key={t.id} t={t} onSaved={(n) => setItems((all) => all.map((x) => (x.id === n.id ? n : x)))} />)}</div>}
    </div>
  );
}
